import { Box } from '@mui/material';
import {
  MinRetentionRuleFrequencyEnum,
  type ControlRules,
} from '@repo/api-gw-sdk';

import { getHumanizeRetentionText } from '@/app/(dashboard)/settings/controls/retentionSelectionPopover';
import { controlsFrequenciesMapping } from '@/app/(dashboard)/settings/frequency';
import { fromDaysToBiggestAsPossible } from '@/utils/dateTime';

import { MaximumRetentionEditor } from '../../app/(dashboard)/settings/controls/MaximumRetentionEditor';
import { MinimumRetentionEditor } from '../../app/(dashboard)/settings/controls/MinimumRetentionEditor';
import { NumberOfCopiesEditor } from '../../app/(dashboard)/settings/controls/NumberOfCopiesEditor';

interface ControlRule {
  enabled: (rules: ControlRules) => boolean;
  onToggle: (
    e: React.ChangeEvent<HTMLInputElement>,
    rules: ControlRules
  ) => ControlRules;
  label: (rules: ControlRules, editMode?: boolean) => React.JSX.Element;
  icon: string;
  Editor?: React.ComponentType<{
    rules: ControlRules;
    onChange: (rules: ControlRules) => void;
  }>;
}

const numOfCopiesRule = {
  enabled: (rules: ControlRules) => !!rules.numberOfCopies?.enabled,
  onToggle: (e: React.ChangeEvent<HTMLInputElement>, rules: ControlRules) => ({
    numberOfCopies: {
      enabled: e.target.checked,
      minCopies: e.target.checked ? rules.numberOfCopies?.minCopies || 2 : 2,
    },
  }),
  Editor: NumberOfCopiesEditor,
  label: (rules: ControlRules, editMode?: boolean) => (
    <>
      <b>Number of copies</b> is at least
      {editMode ? '...' : <b> {rules.numberOfCopies?.minCopies || 1}</b>}
    </>
  ),
  icon: 'material-symbols-content-copy-outline',
};

const crossRegionRule = {
  enabled: (rules: ControlRules) => !!rules.crossRegion,
  onToggle: (e: React.ChangeEvent<HTMLInputElement>) => ({
    crossRegion: e.target.checked,
  }),
  label: () => (
    <>
      At least one copy is in a <b>different region</b> from the source
    </>
  ),
  icon: 'material-symbols-globe',
};

const crossAccountRule = {
  enabled: (rules: ControlRules) => !!rules.crossAccount,
  onToggle: (e: React.ChangeEvent<HTMLInputElement>) => ({
    crossAccount: e.target.checked,
  }),
  label: () => (
    <>
      At least one copy is in a <b>different account</b> from the source
    </>
  ),
  icon: 'material-symbols-person-outline',
};

const crossCloudProviderRule = {
  enabled: (rules: ControlRules) => !!rules.crossCloudProvider,
  onToggle: (e: React.ChangeEvent<HTMLInputElement>) => ({
    crossCloudProvider: e.target.checked,
  }),
  label: () => (
    <>
      At least one copy is in a <b>different cloud provider</b> from the source
    </>
  ),
  icon: 'material-symbols-filter-drama-outline',
};

const defaultMaxRetention = 30;
const maximumRetentionRule = {
  enabled: (rules: ControlRules) => !!rules.maximumRetention?.enabled,
  onToggle: (e: React.ChangeEvent<HTMLInputElement>, rules: ControlRules) => ({
    maximumRetention: {
      enabled: e.target.checked,
      maximumRetention: e.target.checked
        ? rules.maximumRetention?.maximumRetention || defaultMaxRetention
        : defaultMaxRetention,
    },
  }),
  Editor: MaximumRetentionEditor,
  label: (rules: ControlRules, editMode?: boolean) => {
    const retention =
      rules.maximumRetention?.maximumRetention || defaultMaxRetention;
    return (
      <>
        All snapshots have <b>maximum retention</b> of
        {/* here */}
        {editMode ? (
          '...'
        ) : (
          <b>
            {' '}
            {getHumanizeRetentionText(retention)}
            {fromDaysToBiggestAsPossible(retention).unit === 'days'
              ? ''
              : ` (${retention} days)`}
          </b>
        )}
      </>
    );
  },
  icon: 'material-symbols-hourglass-bottom',
};

const minimumRetentionRule = {
  enabled: (rules: ControlRules) =>
    !!rules.minimumRetention?.some((r) => r.enabled),
  onToggle: (e: React.ChangeEvent<HTMLInputElement>) => ({
    minimumRetention: e.target.checked
      ? [
          {
            enabled: e.target.checked,
            minimumRetention:
              controlsFrequenciesMapping[MinRetentionRuleFrequencyEnum.Daily]
                .minimumRetentionInDays,
            frequency: MinRetentionRuleFrequencyEnum.Daily,
          },
        ]
      : [],
  }),
  Editor: MinimumRetentionEditor,
  label: (rules: ControlRules, editMode?: boolean) => (
    <>
      At least one copy has a <b>minimum retention</b> of...
      {!editMode && (
        <>
          {' '}
          {rules.minimumRetention
            ?.filter((r) => r.enabled)
            .map(({ minimumRetention, frequency }, i) => {
              const { text } = controlsFrequenciesMapping[frequency];
              return (
                <Box key={i} className='my-[8px]'>
                  <b>{text}</b> are retained for at least{' '}
                  <b>
                    {getHumanizeRetentionText(minimumRetention)}{' '}
                    {fromDaysToBiggestAsPossible(minimumRetention).unit ===
                    'days'
                      ? ''
                      : ` (${minimumRetention} days)`}
                  </b>
                </Box>
              );
            })}
        </>
      )}
    </>
  ),
  icon: 'material-symbols-date-range',
};

export const controlRules: ControlRule[] = [
  numOfCopiesRule,
  crossRegionRule,
  crossAccountRule,
  crossCloudProviderRule,
  maximumRetentionRule,
  minimumRetentionRule,
];
